import { useEffect, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Paper, IconButton, Box, Chip } from '@mui/material';
import { GripVertical, Trash2 } from 'lucide-react';
import { ButtonEditModal } from './ButtonEditModal';
import { ButtonType } from './interface';

interface ButtonInfo {
  buttonId: number;
  label: string;
  type: ButtonType;
  url?: string | null;
  callback?: string | null;
  keyboardType?: 'inline' | 'reply';
  nextBlockId?: number | null;
  isEditable?: boolean;
}

export interface ButtonBlock {
  id: number;
  blockId: number;
  Button: ButtonInfo;
  isFullWidth?: boolean;
  rowOrder?: number;
  orderX?: number;
  orderY?: number;
}

interface Props {
  block: { ButtonsToBlocks: ButtonBlock[] };
  onChange?: (updated: ButtonBlock[]) => void;
}

interface SortableItemProps {
  item: ButtonBlock;
  onClick: (item: ButtonBlock) => void;
  onDelete: (item: ButtonBlock) => void;
}

const TYPE_LABELS: Record<string, string> = {
  none: 'Без дії',
  url: 'Посилання',
  web_app: 'Web App посилання',
  callback: 'Обробка у боті',
  programmed: 'Програмна кнопка',
};

const KEYBOARD_LABELS: Record<string, string> = {
  inline: 'Під повідомленням',
  reply: 'Під клавіатурою користувача',
};

const API_URL = import.meta.env.VITE_API_URL;

function SortableItem({ item, onClick, onDelete }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        p: 2,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        justifyContent: 'space-between',
      }}
      onClick={() => onClick(item)}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1}>
        <IconButton {...attributes} {...listeners} sx={{ p: 0.5 }}>
          <GripVertical size={16} />
        </IconButton>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <span>{item.Button.label}</span>

          {item.Button.type && (
            <Chip
              label={TYPE_LABELS[item.Button.type] || item.Button.type}
              size="small"
              color="primary"
            />
          )}

          {item.Button.keyboardType && (
            <Chip
              label={
                KEYBOARD_LABELS[item.Button.keyboardType] ||
                item.Button.keyboardType
              }
              size="small"
              color="success"
            />
          )}
        </Box>
      </Box>

      {item.Button.isEditable !== false && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item);
          }}
          sx={{
            bgcolor: 'error.main',
            '&:hover': { bgcolor: 'error.dark' },
            p: 0.5,
          }}
        >
          <Trash2 size={16} color="white" />
        </IconButton>
      )}
    </Paper>
  );
}

export function BlockButtonsTab({ block, onChange }: Props) {
  const [items, setItems] = useState<ButtonBlock[]>([]);
  const [editing, setEditing] = useState<ButtonBlock | null>(null);

  useEffect(() => {
    setItems(block.ButtonsToBlocks || []);
  }, [block.ButtonsToBlocks]);

  const saveOrder = async (updated: ButtonBlock[]) => {
    await fetch(
      `${API_URL}/content/block/${updated[0].blockId}/buttons-order`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: updated.map((item, index) => ({
            id: item.id,
            rowOrder: index,
          })),
        }),
      },
    );
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      const updated = arrayMove(items, oldIndex, newIndex);
      setItems(updated);
      await saveOrder(updated);
      onChange?.(updated);
    }
  };

  const handleDelete = async (item: ButtonBlock) => {
    await fetch(`${API_URL}/content/button/${item.id}`, {
      method: 'DELETE',
    });

    const updated = items.filter((i) => i.id !== item.id);
    setItems(updated);
    onChange?.(updated);
  };

  const handleSaved = (updated: ButtonBlock) => {
    const updatedItems = items.map((i) => (i.id === updated.id ? updated : i));
    setItems(updatedItems);
    onChange?.(updatedItems);
    setEditing(null);
  };

  return (
    <>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              onClick={setEditing}
              onDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>

      {editing && (
        <ButtonEditModal
          buttonLink={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
